import clsx from "clsx";
import { FC, useEffect, useRef } from "react";

import styles from "./AutogrowText.module.css";

interface AutogrowTextProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  // Optional ceiling — capped textareas show their own scrollbar above
  // this height. Omit to let the textarea grow to fit content; callers
  // are expected to handle overall scrolling at a higher level
  // (e.g. the EditMetadataDialog has a scroll wrapper around the
  // whole metadata table).
  maxHeightPx?: number;
}

// A single-line-by-default textarea that grows with its content. By
// default there's no upper bound on height — the surrounding container
// handles scrolling — so a long JSON value renders at full height
// rather than embedding its own inner scrollbar.
export const AutogrowText: FC<AutogrowTextProps> = ({
  value,
  onChange,
  disabled,
  maxHeightPx,
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let lastApplied = "";
    const measure = () => {
      // Reset before reading so scrollHeight reflects the content's
      // natural height regardless of any previously-applied inline
      // height (otherwise scrollHeight clamps to the current size,
      // and the textarea never shrinks).
      el.style.height = "0px";
      const target =
        maxHeightPx != null
          ? `${Math.min(el.scrollHeight, maxHeightPx)}px`
          : `${el.scrollHeight}px`;
      if (target !== lastApplied) {
        lastApplied = target;
        el.style.height = target;
      } else {
        // Restore so we don't leave the box at 0 while waiting for
        // a no-op confirmation.
        el.style.height = lastApplied;
      }
    };
    measure();
    // The textarea may mount while its Modal ancestor is `display:
    // none`, in which case `scrollHeight` reads as 0 and the first
    // measurement is wrong. ResizeObserver re-fires once the modal
    // becomes visible (and the textarea picks up real dimensions),
    // at which point we measure again and apply the correct height.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value, maxHeightPx]);

  return (
    <textarea
      ref={ref}
      className={clsx("form-control", styles.textarea)}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      spellCheck={false}
      rows={1}
    />
  );
};
